import { apiClient } from './api-client';
import type { Ticket, PaginatedResponse, PaginationParams } from '@centraoneplus/shared-types';

export type InboxView = 'all' | 'assigned_to_me' | 'unassigned' | 'mentioned' | 'sla_breach';

export interface InboxFilters extends PaginationParams {
  view?: InboxView;
  status?: string | string[];
  priority?: string | string[];
  channel?: string | string[];
  search?: string;
  /** Queue order: 'newest' (latest ticket first) or 'sla' (deadline first). */
  sort?: InboxSort;
}

export type InboxSort = 'newest' | 'sla';

export interface InboxStats {
  total: number;
  new: number;
  open: number;
  pending: number;
  assignedToMe: number;
  unassigned: number;
  slaBreaching: number;
  /**
   * One count per raw channel value over the same set as `total`. Optional:
   * an older BFF does not send it, and the channel tabs then render without a
   * count rather than with a zero they cannot prove.
   */
  byChannel?: Record<string, number>;
}

export interface InboxItem extends Ticket {
  /** First 150 chars of the description — the BFF sends this, not `description`. */
  preview?: string;
  lastMessage?: {
    content: string;
    senderType: string;
    createdAt: string;
  };
  unreadCount: number;
  customer?: {
    id: string;
    name?: string;
    email: string;
  };
  assignee?: {
    id: string;
    name: string;
    email: string;
  };
  /** SLA due date for first response or resolution */
  slaDueAt?: string;
  /** Whether the SLA has been breached */
  slaBreached?: boolean;
}

class InboxService {
  private readonly basePath = '/inbox';

  async getInbox(filters?: InboxFilters): Promise<PaginatedResponse<InboxItem>> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, v));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }

    const response = await apiClient.get<PaginatedResponse<InboxItem>>(
      `${this.basePath}?${params.toString()}`
    );
    return response.data;
  }

  async getStats(): Promise<InboxStats> {
    const response = await apiClient.get<InboxStats>(`${this.basePath}/stats`);
    return response.data;
  }

  async markAsRead(ticketId: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${ticketId}/read`);
  }

  async markAsUnread(ticketId: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${ticketId}/unread`);
  }

  async snooze(ticketId: string, until: Date): Promise<void> {
    await apiClient.post(`${this.basePath}/${ticketId}/snooze`, {
      snoozedUntil: until.toISOString(),
    });
  }

  async star(ticketId: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${ticketId}/star`);
  }

  async unstar(ticketId: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${ticketId}/unstar`);
  }
}

export const inboxService = new InboxService();
